import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import Verify from "@/pages/Verify";
import Home from "@/pages/Home";
import Rides from "@/pages/Rides";
import RideDetail from "@/pages/RideDetail";
import Buddies from "@/pages/Buddies";
import Campus from "@/pages/Campus";
import PostDetail from "@/pages/PostDetail";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import Profile from "@/pages/Profile";
import Notifications from "@/pages/Notifications";
import Chat from "@/pages/Chat";
import Admin from "@/pages/Admin";
import SuperAdmin from "@/pages/SuperAdmin";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AuthCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.slice(1)).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sessionId });
        window.history.replaceState(null, "", window.location.pathname);
        setUser(data);
        navigate(data.institution_id ? "/home" : "/verify", { replace: true });
      } catch {
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-muted-foreground animate-pulse">Signing you in…</p>
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null)
    return <div className="min-h-screen flex items-center justify-center"><p className="text-muted-foreground animate-pulse">Loading…</p></div>;
  if (user === false) return <Navigate to="/login" replace />;
  const needsVerify = user.role === "member" && (!user.institution_id || user.verification_status !== "verified");
  if (needsVerify && location.pathname !== "/verify") return <Navigate to="/verify" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation(); // read hash from here, not window.location.hash (not reactive)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/verify" element={<Protected><Verify /></Protected>} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/home" element={<Home />} />
        <Route path="/rides" element={<Rides />} />
        <Route path="/rides/find" element={<Rides />} />
        <Route path="/rides/offer" element={<Rides />} />
        <Route path="/rides/my" element={<Rides />} />
        <Route path="/rides/:id" element={<RideDetail />} />
        <Route path="/buddies" element={<Buddies />} />
        <Route path="/buddies/requests" element={<Buddies />} />
        <Route path="/campus" element={<Campus />} />
        <Route path="/campus/confessions" element={<Campus />} />
        <Route path="/campus/post/:id" element={<PostDetail />} />
        <Route path="/events" element={<Events />} />
        <Route path="/events/:id" element={<EventDetail />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/settings" element={<Profile />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/chat" element={<Chat />} />
        <Route path="/admin/*" element={<Admin />} />
        <Route path="/super-admin/*" element={<SuperAdmin />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
